import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { shareReplay, take } from 'rxjs/operators';
import { ErrorResponse } from 'src/app/api/models';
import { ErrorRequest } from 'src/app/api/models/error-request';
import { ErrorsService } from 'src/app/api/services';
import { GuidHelper } from '../helpers/guid-helper';
import { AuthService } from '../services/auth/auth.service';
import { CorrelationIdService } from '../services/correlation-id.service';
import { SessionStorageService } from '../services/localstorage/sessionstorage.service';

@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  correlationIdHeaderKey = 'x-correlation-id';

  constructor(
    private locationStrategy: LocationStrategy,
    private errorsService: ErrorsService,
    private authService: AuthService,
    private correlationIdService: CorrelationIdService,
    private sessionStorageService: SessionStorageService,
    private router: Router
  ) {}

  log(error: Error | HttpErrorResponse): Observable<ErrorResponse> {
    const errorToSend = this.addContextInfo(error);

    if (error instanceof HttpErrorResponse) {
      this.correlationIdService.setCorrelationId(this.tryExtractCorrelationId(error));
    }

    const result$ = this.errorsService.post(errorToSend).pipe(take(1), shareReplay());

    result$.subscribe();

    return result$;
  }

  private addContextInfo(error: Error | HttpErrorResponse): ErrorRequest {
    const appId = 'PublicSpa';
    const user = {
      name:
        this.authService.user && this.authService.user.profile
          ? this.authService.user.profile.name
          : 'anonymous',
      sid:
        this.authService.user && this.authService.user.profile
          ? this.authService.user.profile.sid
          : 'not available',
    };
    const time = new Date().getTime();
    const id = `${appId}-${user.name}-${time}`;
    const location = this.locationStrategy;
    const url = location instanceof PathLocationStrategy ? location.path() : '';
    const userAgent = navigator.userAgent;

    const meta: any = {};
    if (error instanceof Error) {
      meta.message = error.message;
      meta.stack = error.stack;
    } else if (error instanceof HttpErrorResponse) {
      meta.error = error.error;
      meta.message = error.message;
      meta.name = error.name;
      meta.status = error.status;
      meta.statusText = error.statusText;
      meta.url = error.url;
    }

    const errorToSend: ErrorRequest = {
      id,
      appId,
      user,
      time,
      url,
      userAgent,
      meta: meta,
    };

    return errorToSend;
  }

  tryExtractCorrelationId(response: HttpErrorResponse): any {
    if (response.headers.has(this.correlationIdHeaderKey)) {
      return response.headers.get(this.correlationIdHeaderKey);
    }

    return null;
  }

  logToStorage(error: Error): string {
    const errorId = GuidHelper.createGuid();
    const storageKey = this.getStorageKey(errorId);
    const errorRequest = this.addContextInfo(error);
    this.sessionStorageService.set(storageKey, errorRequest);

    return errorId;
  }

  getFromStorage(errorId: string): Observable<ErrorRequest> {
    const storageKey = this.getStorageKey(errorId);

    return this.sessionStorageService.select<ErrorRequest>(storageKey);
  }

  handlePageLoadError(errorResponse: HttpErrorResponse) {
    this.router.navigate(['/error'], {
      queryParams: {
        httpStatusCode: errorResponse.status,
        date: new Date(),
        correlationid:
          errorResponse.status >= 500 ? this.tryExtractCorrelationId(errorResponse) : undefined,
      },
      skipLocationChange: errorResponse.status >= 400 && errorResponse.status < 500,
    });
  }

  private getStorageKey(errorId: string): string {
    return `error-${errorId}`;
  }
}
